/**
 * Lightweight models used by the docuty-template-renderer library.
 *
 * These interfaces are intentionally minimal: they describe only the
 * properties that the renderer actually needs. Any object from the API
 * layer (FormJson, FormLevelJson, FormFieldJson, …) satisfies these
 * interfaces by structural typing, so no casting is required at the
 * call site.
 */

// ---------------------------------------------------------------------------
// Field type
// ---------------------------------------------------------------------------

export type RendererFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'time'
  | 'select'
  | 'checkbox'
  | 'photoPicker'
  | 'divider';

/** Const-object mirror of RendererFieldType for use in switch/comparisons. */
export const RendererFieldType = {
  Text:        'text'        as RendererFieldType,
  Number:      'number'      as RendererFieldType,
  Date:        'date'        as RendererFieldType,
  Time:        'time'        as RendererFieldType,
  Select:      'select'      as RendererFieldType,
  Checkbox:    'checkbox'    as RendererFieldType,
  PhotoPicker: 'photoPicker' as RendererFieldType,
  Divider:     'divider'     as RendererFieldType,
} as const;

// ---------------------------------------------------------------------------
// Form structure
// ---------------------------------------------------------------------------

/** A single form field as seen by the renderer. */
export interface RendererField {
  id?:     string;
  type?:   RendererFieldType;
  label?:  string;
  hint?:   string | null;
  hidden?: boolean | null;
}

/** A named level (sub-section) within a form. */
export interface RendererLevel {
  id?:          string;
  name?:        string;
  description?: string | null;
  fields?:      RendererField[];
  children?:    RendererLevel[] | null;
}

/**
 * The root of a form (audit form or entry form).
 * Compatible with the API's FormJson shape.
 */
export interface RendererForm {
  id?:      string;
  fields?:  RendererField[];
  children?: RendererLevel[] | null;
}

// ---------------------------------------------------------------------------
// Navigation state
// ---------------------------------------------------------------------------

/** Tracks which level is currently shown in the phone preview. */
export interface RendererSelectedNode {
  level: RendererForm | RendererLevel;
  title: string;
}
